import { useState, useCallback, useRef, useEffect } from "react";
import { GetAppointmentStats } from "../GetAppointmentStats";
import { oids } from "@/configs/Settings";

interface UseAppointmentStatsProps {
  /** IDs dos locais para filtrar */
  actors: string[];
  /** ID do local selecionado */
  selectedLocation?: string;
  /** ID do profissional selecionado */
  selectedPractitioner?: string;
  /** Data inicial do período (opcional) */
  startDate?: string;
  /** Data final do período (opcional) */
  endDate?: string;
  /** Propósito do agendamento (opcional) */
  purposeOfUse?: string;
  /** Status dos agendamentos (opcional) */
  status?: string[];
  /** Indica se deve retornar apenas o resumo (opcional) */
  summary?: boolean;
  /** Quantidade de registros a retornar (opcional) */
  count?: number;
  /** Filtrar por supporting-info ausente (opcional) */
  supportingInfoMissing?: boolean;
  /** Quando verdadeiro, usa _include=Appointment:actor:HealthcareService (opcional) */
  includeHealthcareService?: boolean;

  /** Quando verdadeiro, usa _include=Appointment:encounter (opcional) */
  includeEncounter?: boolean;
}

interface UseAppointmentStatsResult {
  /** Dados dos agendamentos */
  data: {
    appointments?: fhir5.Appointment[];
    patients?: fhir5.Patient[];
    healthcareServices?: fhir5.HealthcareService[];
    encounters?: fhir5.Encounter[];
    total?: number;
  };
  /** Estado de carregamento */
  loading: boolean;
  /** Estado de erro */
  error: Error | null;
  /** Função para buscar os dados */
  fetchStats: () => Promise<void>;
}

/**
 * Extrai apenas os recursos do tipo Appointment do bundle
 * @param bundle Bundle retornado pela API
 * @returns Array de Appointment
 */
function extractAppointments(bundle?: fhir5.Bundle): fhir5.Appointment[] {
  if (!bundle?.entry) return [];

  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.Appointment> =>
        entry.resource?.resourceType === "Appointment"
    )
    .map((entry) => entry.resource!)
    .filter(
      (resource): resource is fhir5.Appointment => resource !== undefined
    );
}

function extractEncounters(bundle?: fhir5.Bundle): fhir5.Encounter[] {
  if (!bundle?.entry) return [];

  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.Encounter> =>
        entry.resource?.resourceType === "Encounter"
    )
    .map((entry) => entry.resource!)
    .filter((resource): resource is fhir5.Encounter => resource !== undefined);
}
/**
 * Extrai apenas os recursos do tipo Patient do bundle
 * @param bundle Bundle retornado pela API
 * @returns Array de Patient
 */
function extractPatients(bundle?: fhir5.Bundle): fhir5.Patient[] {
  if (!bundle?.entry) return [];

  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.Patient> =>
        entry.resource?.resourceType === "Patient"
    )
    .map((entry) => entry.resource!)
    .filter((resource): resource is fhir5.Patient => resource !== undefined);
}

/**
 * Extrai apenas os recursos do tipo HealthcareService do bundle
 * @param bundle Bundle retornado pela API
 * @returns Array de HealthcareService
 */
function extractHealthcareServices(
  bundle?: fhir5.Bundle
): fhir5.HealthcareService[] {
  if (!bundle?.entry) return [];

  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.HealthcareService> =>
        entry.resource?.resourceType === "HealthcareService"
    )
    .map((entry) => entry.resource!)
    .filter(
      (resource): resource is fhir5.HealthcareService => resource !== undefined
    );
}

/**
 * Hook para buscar estatísticas de agendamentos
 *
 * @example
 * ```tsx
 * const { data, loading, error, fetchStats } = useAppointmentStats({
 *   actors: ['Location/1', 'Location/2'],
 *   startDate: '2024-01-01',
 *   endDate: '2024-12-31',
 *   status: ['booked', 'arrived'],
 *   summary: true
 * });
 *
 * // Buscar dados
 * useEffect(() => {
 *   if (actors.length) {
 *     fetchStats();
 *   }
 * }, [actors, fetchStats]);
 * ```
 */
export function useAppointmentStats(
  props: UseAppointmentStatsProps
): UseAppointmentStatsResult {
  const [data, setData] = useState<{
    appointments?: fhir5.Appointment[];
    patients?: fhir5.Patient[];
    healthcareServices?: fhir5.HealthcareService[];
    encounters?: fhir5.Encounter[];
    total?: number;
  }>({
    appointments: [],
    patients: [],
    healthcareServices: [],
    encounters: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Mantém uma referência estável dos parâmetros
  const paramsRef = useRef(props);

  // Atualiza a referência quando as props mudam
  useEffect(() => {
    paramsRef.current = props;
  }, [props]);

  const fetchStats = useCallback(async () => {
    const currentParams = paramsRef.current;

    // Evita chamada se não houver actors
    if (!currentParams.actors.length) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await GetAppointmentStats({
        actor: currentParams.selectedLocation
          ? `Location/${currentParams.selectedLocation}`
          : currentParams.actors.join(","),
        professionalActor: currentParams.selectedPractitioner
          ? `Practitioner/${oids.cpf}-${currentParams.selectedPractitioner
              ?.split("-")
              .pop()}`
          : undefined,
        startDate: currentParams.startDate,
        endDate: currentParams.endDate,
        purposeOfUse: currentParams.purposeOfUse,
        status: currentParams.status,
        summary: currentParams.summary,
        count: currentParams.count,
        supportingInfoMissing: currentParams.supportingInfoMissing,
        includeHealthcareService: currentParams.includeHealthcareService,
        includeEncounter: currentParams.includeEncounter,
      });

      if (currentParams.summary) {
        setData({
          appointments: [],
          patients: [],
          healthcareServices: [],
          encounters: [],
          total: response.total,
        });
      } else {
        // Extrai Appointments e Patients do bundle
        const appointments = extractAppointments(response);
        const patients = extractPatients(response);
        const healthcareServices = extractHealthcareServices(response);
        const encounters = extractEncounters(response);

        setData({
          appointments,
          patients,
          healthcareServices,
          encounters,
          total: response.total,
        });
      }
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Erro ao buscar estatísticas")
      );
      console.error("useAppointmentStats error:", err);
    } finally {
      setLoading(false);
    }
  }, []); // fetchStats não tem dependências externas

  return {
    data,
    loading,
    error,
    fetchStats,
  };
}
